import React, { FC, useCallback, useEffect, useRef } from 'react';
import {
  Grid,
  Container,
  Dialog,
  Paper,
  Typography,
  Button,
  Box,
  TextField,
  MenuItem,
  CircularProgress,
  useMediaQuery,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useTheme } from '@mui/material/styles';
import { useAiConfigContext } from './context';
import { Header } from './components/header';
import { PromptsTable } from './components/prompts-table';
import { AiConfigForm } from './components/ai-config-form';
import { Loader } from './components/loader';
import { DeleteConfirmationModal } from 'components/delete-confirmation';
import ModalActionBar from 'components/modal-action-bar';
import { useTranslation } from 'react-i18next';

import { AiUpdateOptionsFormData, Prompt } from './types';

const useStyles = makeStyles((theme) => ({
  rootContainer: {
    padding: theme.spacing(2),
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(1),
    },
  },
  configPaper: {
    padding: theme.spacing(2),
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(1.5),
    },
  },
  configHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
    gap: theme.spacing(1),
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
      alignItems: 'stretch',
      marginBottom: theme.spacing(1),
    },
  },
  configHeaderTitle: {
    [theme.breakpoints.down('md')]: {
      marginBottom: theme.spacing(0.5),
    },
  },
  configButton: {
    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
  },
  systemPromptPreview: {
    maxHeight: '200px',
    overflow: 'auto',
    [theme.breakpoints.down('md')]: {
      maxHeight: '100px',
    },
  },
  systemPromptText: {
    [theme.breakpoints.down('md')]: {
      wordBreak: 'break-word',
    },
  },
  promptsHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(2, 2, 0, 2),
    gap: theme.spacing(1),
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
      alignItems: 'stretch',
      padding: theme.spacing(1.5, 1.5, 0, 1.5),
    },
  },
  promptsHeaderTitle: {
    [theme.breakpoints.down('md')]: {
      marginBottom: theme.spacing(0.5),
    },
  },
  promptsButton: {
    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
  },
  dialogPaper: {
    height: '90vh',
  },
  promptModalContent: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  promptModalFields: {
    flex: 1,
    overflowY: 'auto',
    padding: theme.spacing(2),
  },
  promptModalActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 2),
    borderTop: `1px solid ${theme.palette.divider}`,
    flexShrink: 0,
  },
  promptActionButton: {
    [theme.breakpoints.down('md')]: {
      flex: 1,
    },
  },
}));

/* eslint-disable no-console */
const logError = (message: string, err: unknown) => {
  if (process.env.NODE_ENV !== 'production') {
    console.error(message, err);
  }
};
/* eslint-enable no-console */

interface AiConfigModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: AiUpdateOptionsFormData) => Promise<void>;
  isSubmitting: boolean;
}

const AiConfigModal: FC<AiConfigModalProps> = ({ open, onClose, onSubmit, isSubmitting }: AiConfigModalProps) => {
  const classes = useStyles();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { aiOptions, availableModels } = useAiConfigContext();

  const handleSubmit = async (data: AiUpdateOptionsFormData) => {
    await onSubmit(data);
    onClose();
  };

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      fullScreen={fullScreen}
      classes={{ paper: fullScreen ? undefined : classes.dialogPaper }}
    >
      <AiConfigForm
        aiOptions={aiOptions}
        availableModels={availableModels}
        onCancel={onClose}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
      />
    </Dialog>
  );
};

export const AiConfigScreen: FC = () => {
  const classes = useStyles();
  const theme = useTheme();
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const {
    prompts,
    aiOptions,
    availableModels,
    isLoading,
    isError,
    error,
    createPrompt,
    updatePrompt,
    deletePrompt,
    updateAiOptions,
    refreshAiOptions,
  } = useAiConfigContext();

  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [promptModalType, setPromptModalType] = React.useState<'create' | 'edit'>('create');
  const [editingPrompt, setEditingPrompt] = React.useState<Prompt | null>(null);
  const [isSaving, setIsSaving] = React.useState(false);
  const [aiConfigOpen, setAiConfigOpen] = React.useState(false);
  const [deletePromptId, setDeletePromptId] = React.useState<string | null>(null);
  const [isDeleting, setIsDeleting] = React.useState(false);

  const openAiConfigModal = useCallback(() => {
    setAiConfigOpen(true);
  }, []);

  const closeAiConfigModal = useCallback(() => {
    setAiConfigOpen(false);
  }, []);

  const handleNewPrompt = useCallback(() => {
    setPromptModalType('create');
    setEditingPrompt(null);
    setIsModalOpen(true);
  }, []);

  const handleEditPrompt = useCallback((prompt: Prompt) => {
    setPromptModalType('edit');
    setEditingPrompt(prompt);
    setIsModalOpen(true);
  }, []);

  const handleDeletePrompt = useCallback((promptId: string) => {
    setDeletePromptId(promptId);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!deletePromptId) return;
    setIsDeleting(true);
    try {
      await deletePrompt(deletePromptId);
      setDeletePromptId(null);
    } catch (err) {
      logError('Failed to delete prompt', err);
    } finally {
      setIsDeleting(false);
    }
  }, [deletePrompt, deletePromptId]);

  const handleCloseDelete = useCallback(() => {
    setDeletePromptId(null);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setEditingPrompt(null);
  }, []);

  const handleSavePrompt = useCallback(async (data: any) => {
    setIsSaving(true);
    try {
      if (promptModalType === 'create') {
        await createPrompt(data);
      } else {
        await updatePrompt({
          promptId: editingPrompt?._id || '',
          ...data,
        });
      }
      handleCloseModal();
    } catch (err) {
      logError('Failed to save prompt', err);
      throw err;
    } finally {
      setIsSaving(false);
    }
  }, [createPrompt, updatePrompt, editingPrompt, promptModalType, handleCloseModal]);

  const handleAiConfigSubmit = useCallback(async (data: AiUpdateOptionsFormData) => {
    setIsSaving(true);
    try {
      await updateAiOptions(data);
      closeAiConfigModal();
    } catch (err) {
      logError('Failed to save AI config', err);
      throw err;
    } finally {
      setIsSaving(false);
    }
  }, [updateAiOptions, closeAiConfigModal]);

  const renderPromptForm = () => (
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    <PromptForm
      prompt={editingPrompt}
      onCancel={handleCloseModal}
      onSubmit={handleSavePrompt}
    />
  );

  if (isLoading) {
    return <Loader />;
  }

  if (isError) {
    return (
      <Container>
        <Paper>
          <Typography color="error" variant="body1">
            {error?.message || 'Failed to load AI configuration'}
          </Typography>
          <Button onClick={refreshAiOptions} color="secondary" style={{ marginTop: '10px' }}>
            Try Again
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Box className={classes.rootContainer}>
      <Container maxWidth="lg">
        <Grid container spacing={isMobile ? 1 : 2}>
          <Grid item xs={12}>
            <Paper>
              <Header />
            </Paper>
          </Grid>
          <Grid item xs={12}>
            <Paper className={classes.configPaper}>
              <Box className={classes.configHeader}>
                <Typography variant="h6" className={classes.configHeaderTitle}>{t('aiConfig:mainConfig')}</Typography>
                <Button onClick={openAiConfigModal} variant="contained" color="secondary" className={classes.configButton} style={{ textTransform: 'none' }}>
                  {t('aiConfig:configureAI')}
                </Button>
              </Box>
              <Grid container spacing={isMobile ? 1 : 2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">{t('aiConfig:modelAgent')}</Typography>
                  <Typography variant="body1">{aiOptions?.model_agent || '-'}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="textSecondary">{t('aiConfig:promptSystem')}</Typography>
                  <Paper variant="outlined" className={classes.systemPromptPreview} style={{ padding: theme.spacing(1.5), marginTop: theme.spacing(1) }}>
                    <Typography className={classes.systemPromptText} style={{ fontFamily: 'monospace', fontSize: '0.8rem', whiteSpace: 'pre-wrap' }}>
                      {aiOptions?.prompt_system?.english || '-'}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
          <Grid item xs={12}>
            <Paper>
              <Box className={classes.promptsHeader}>
                <Typography variant="h6" className={classes.promptsHeaderTitle}>{t('aiConfig:prompts')}</Typography>
                <Button onClick={handleNewPrompt} variant="contained" color="secondary" className={classes.promptsButton} style={{ textTransform: 'none' }}>
                  {t('aiConfig:newPrompt')}
                </Button>
              </Box>
              <PromptsTable
                prompts={prompts}
                isLoading={isLoading}
                onEdit={handleEditPrompt}
                onDelete={handleDeletePrompt}
              />
            </Paper>
          </Grid>
        </Grid>

        {isModalOpen && renderPromptForm()}

        <AiConfigModal
          open={aiConfigOpen}
          onClose={closeAiConfigModal}
          onSubmit={handleAiConfigSubmit}
          isSubmitting={isSaving}
        />

        <DeleteConfirmationModal
          title={t('aiConfig:deletePrompt')}
          confirmationMessage={t('aiConfig:confirmDelete')}
          isOpen={!!deletePromptId}
          onConfirm={handleConfirmDelete}
          onClose={handleCloseDelete}
          isRemoveLoading={isDeleting}
          buttonConfirmText={t('aiConfig:delete')}
        />
      </Container>
    </Box>
  );
};

interface PromptFormProps {
  prompt: Prompt | null;
  onCancel: () => void;
  onSubmit: (data: any) => Promise<void>;
}

const PromptForm: FC<PromptFormProps> = ({ prompt, onCancel, onSubmit }) => {
  const { t } = useTranslation();
  const { availableModels } = useAiConfigContext();

  const [formData, setFormData] = React.useState({
    name: prompt?.name || '',
    model: prompt?.model || '',
    english: {
      description: prompt?.english?.description || '',
      content: prompt?.english?.content || '',
    },
  });

  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const isValid = formData.name.trim() !== '' && formData.model !== '' &&
    formData.english.description.trim() !== '' && formData.english.content.trim() !== '';

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleNestedChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      english: { ...prev.english, [field]: value },
    }));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await onSubmit(formData);
    } finally {
      if (isMountedRef.current) {
        setIsSubmitting(false);
      }
    }
  };

  const classes = useStyles();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Dialog
      open
      fullWidth
      maxWidth="sm"
      fullScreen={fullScreen}
      onClose={isSubmitting ? undefined : onCancel}
      classes={{ paper: fullScreen ? undefined : classes.dialogPaper }}
    >
      <Box className={classes.promptModalContent}>
        <Typography variant="h6" gutterBottom style={{ padding: theme.spacing(1, 2, 0, 2) }}>
          {prompt ? t('aiConfig:editPrompt') : t('aiConfig:newPrompt')}
        </Typography>

        <Box className={classes.promptModalFields}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('aiConfig:promptName')}
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                margin="normal"
                required
                disabled={isSubmitting}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                select
                label={t('aiConfig:promptModel')}
                value={formData.model}
                onChange={(e) => handleChange('model', e.target.value)}
                margin="normal"
                required
                disabled={isSubmitting}
              >
                {availableModels?.map((model) => (
                  <MenuItem key={model.id} value={model.id}>{model.id}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle1">{t('aiConfig:english')}</Typography>
              <TextField
                fullWidth
                label={t('aiConfig:description')}
                value={formData.english.description}
                onChange={(e) => handleNestedChange('description', e.target.value)}
                margin="normal"
                multiline
                minRows={2}
                maxRows={6}
                required
                disabled={isSubmitting}
              />
              <TextField
                fullWidth
                label={t('aiConfig:content')}
                value={formData.english.content}
                onChange={(e) => handleNestedChange('content', e.target.value)}
                margin="normal"
                multiline
                minRows={4}
                maxRows={12}
                required
                disabled={isSubmitting}
              />
            </Grid>
          </Grid>
        </Box>

        <ModalActionBar
          onCancel={onCancel}
          submitType="button"
          submitText={prompt ? t('aiConfig:update') : t('aiConfig:create')}
          onSubmit={handleSubmit}
          isSubmitting={isSubmitting}
          submitDisabled={!isValid}
        />
      </Box>
    </Dialog>
  );
};